// Element anchors for feedback pins: how a pin finds "the element the author clicked" again.
//
// An anchor keeps a CSS selector path, the element's id-like `data-*` attributes, a text snippet, the
// tag, and the element's box in page coordinates with the click point inside it. Resolution tries, in
// order: the data ids, the selector (when its text still matches), the text among elements with the
// same tag, then the stored position when the page is laid out the same (same width, ±10%). A pin
// that resolves to nothing goes to the side list.

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Anchor {
  selector: string;
  data: Record<string, string>;
  /** Loan boundary context, never used to locate the clicked element. */
  subject?: { loan_id: string | null; core_loan_id: string | null } | null;
  text?: string | null;
  tag: string;
  /** The element's box in page coordinates (viewport box + scroll). */
  rect: Rect;
  /** Where in the box the pin sits, as fractions (0-1). */
  point?: { x: number; y: number } | null;
}

export type Resolution =
  | { kind: "element"; el: Element; how: "data" | "selector" | "text" }
  | { kind: "position"; rect: Rect }
  | { kind: "missing" };

/** Elements that belong to the feedback UI itself: never anchored, never captured. */
export const FEEDBACK_UI_ATTR = "data-feedback-ui";

const ID_LIKE = /^data-(?:[a-z0-9-]*-)?(?:id|key|testid|row|col|field)$/;
const MAX_TEXT = 80;
const MAX_DEPTH = 12;

export function isFeedbackUi(el: Element | null): boolean {
  return !!el?.closest(`[${FEEDBACK_UI_ATTR}]`);
}

function cssEscape(v: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") return CSS.escape(v);
  return v.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c.codePointAt(0)!.toString(16)} `);
}

/** Ids that look hand-written (not React/Radix generated like `:r3:` or `radix-12`). */
function stableId(id: string): boolean {
  return /^[A-Za-z][A-Za-z0-9_-]{0,63}$/.test(id) && !/^radix-|\d{3,}/.test(id);
}

export function normalizeText(s: string | null | undefined): string {
  return (s ?? "").replace(/\s+/g, " ").trim();
}

/** The short text a person would recognise the element by. */
export function textSnippet(el: Element): string {
  const label = el.getAttribute("aria-label") || el.getAttribute("title") || el.getAttribute("placeholder");
  const t = normalizeText(label || el.textContent);
  return t.slice(0, MAX_TEXT);
}

/** `#id` or `tag:nth-of-type(n)` steps from the nearest stable id (or `body`) down to `el`. */
export function selectorPath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  for (let depth = 0; cur && depth < MAX_DEPTH; depth++) {
    const tag = cur.tagName.toLowerCase();
    if (tag === "body" || tag === "html") {
      parts.unshift(tag);
      break;
    }
    if (cur.id && stableId(cur.id)) {
      parts.unshift(`#${cssEscape(cur.id)}`);
      break;
    }
    const parent: Element | null = cur.parentElement;
    if (!parent) {
      parts.unshift(tag);
      break;
    }
    const same = Array.from(parent.children).filter((c) => c.tagName === cur!.tagName);
    parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(cur) + 1})` : tag);
    cur = parent;
  }
  return parts.join(" > ");
}

/** The element's id-like `data-*` attributes (e.g. `data-loan-id`, `data-testid`). */
export function dataIds(el: Element): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of Array.from(el.attributes)) {
    if (ID_LIKE.test(a.name) && a.value && a.value.length <= 200) out[a.name] = a.value;
    if (Object.keys(out).length >= 20) break;
  }
  return out;
}

export interface Scroll {
  x: number;
  y: number;
}

function pageScroll(win: Window | null | undefined): Scroll {
  return { x: win?.scrollX ?? 0, y: win?.scrollY ?? 0 };
}

/** The anchor for `el`, with the click at viewport point (`clientX`, `clientY`) if given. */
export function buildAnchor(el: Element, click?: { clientX: number; clientY: number }): Anchor {
  const r = el.getBoundingClientRect();
  const s = pageScroll(el.ownerDocument.defaultView);
  const point =
    click && r.width > 0 && r.height > 0
      ? { x: clamp01((click.clientX - r.left) / r.width), y: clamp01((click.clientY - r.top) / r.height) }
      : null;
  const text = textSnippet(el);
  return {
    selector: selectorPath(el),
    data: dataIds(el),
    text: text || null,
    tag: el.tagName.toLowerCase(),
    rect: { x: round(r.left + s.x), y: round(r.top + s.y), width: round(r.width), height: round(r.height) },
    point,
  };
}

function clamp01(v: number) {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, Math.round(v * 1000) / 1000)) : 0.5;
}

function round(v: number) {
  return Number.isFinite(v) ? Math.round(v * 10) / 10 : 0;
}

function textMatches(el: Element, want: string | null | undefined): boolean {
  if (!want) return true;
  const have = textSnippet(el);
  if (!have) return false;
  // The stored snippet is cut at 80 characters, and text may have grown or been cut since.
  return have === want || have.startsWith(want) || want.startsWith(have);
}

function usable(el: Element | null): el is Element {
  return !!el && !isFeedbackUi(el);
}

/**
 * Finds the anchored element again in `doc`. `viewportWidth` is the pin's viewport; when the current
 * width is within 10% of it, an anchor whose element is gone falls back to its stored position.
 */
export function resolveAnchor(
  anchor: Anchor,
  doc: Document,
  opts: { viewportWidth?: number; currentWidth?: number } = {},
): Resolution {
  const keys = Object.keys(anchor.data ?? {}).filter((k) => k !== "data-loan-id" && k !== "data-core-loan-id" && k !== "data-feedback-loan-list");
  if (keys.length) {
    const sel = keys.map((k) => `[${k}="${cssEscape(anchor.data[k])}"]`).join("");
    let hits: Element[] = [];
    try {
      hits = Array.from(doc.querySelectorAll(sel)).filter(usable);
    } catch {
      hits = [];
    }
    hits = hits.filter((h) => h.tagName.toLowerCase() === anchor.tag && textMatches(h, anchor.text));
    if (hits.length === 1) return { kind: "element", el: hits[0], how: "data" };
    if (hits.length > 1) {
      const byText = hits.filter((h) => textMatches(h, anchor.text));
      if (byText.length === 1) return { kind: "element", el: byText[0], how: "data" };
    }
  }
  let bySelector: Element | null = null;
  try {
    bySelector = doc.querySelector(anchor.selector);
  } catch {
    bySelector = null;
  }
  if (usable(bySelector) && bySelector.tagName.toLowerCase() === anchor.tag && textMatches(bySelector, anchor.text)) {
    return { kind: "element", el: bySelector, how: "selector" };
  }
  if (anchor.text) {
    const same = Array.from(doc.getElementsByTagName(anchor.tag)).filter((e) => usable(e) && textSnippet(e) === anchor.text);
    if (same.length === 1) return { kind: "element", el: same[0], how: "text" };
    if (same.length > 1) {
      // The one nearest the stored position.
      const s = pageScroll(doc.defaultView);
      const dist = (e: Element) => {
        const r = e.getBoundingClientRect();
        return Math.hypot(r.left + s.x - anchor.rect.x, r.top + s.y - anchor.rect.y);
      };
      const best = same.reduce((a, b) => (dist(b) < dist(a) ? b : a));
      return { kind: "element", el: best, how: "text" };
    }
  }
  const { viewportWidth, currentWidth } = opts;
  if (viewportWidth && currentWidth && Math.abs(currentWidth - viewportWidth) <= viewportWidth * 0.1) {
    return { kind: "position", rect: anchor.rect };
  }
  return { kind: "missing" };
}

/** Where the pin marker goes, in viewport coordinates. */
export function markerPoint(res: Resolution, anchor: Anchor, win: Window): { x: number; y: number } | null {
  const p = anchor.point ?? { x: 1, y: 0 };
  if (res.kind === "element") {
    const r = res.el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return null; // hidden (display: none, collapsed)
    return { x: r.left + r.width * p.x, y: r.top + r.height * p.y };
  }
  if (res.kind === "position") {
    return { x: res.rect.x - win.scrollX + res.rect.width * p.x, y: res.rect.y - win.scrollY + res.rect.height * p.y };
  }
  return null;
}
